/*
  Shared Vuetify 3 prop definitions for the M* compat wrappers.

  Wrappers accept the Vuetify 3 API in both builds and translate it for Vuetify 2
  (see vuetify2Conversions.ts). Props are declared as runtime factories rather than
  types because the Vue 2.7 SFC compiler only accepts a type literal or a same-file
  interface in `defineProps<T>()`; it cannot resolve imported or intersected types.
  Compose them with `defineProps({ ...makeDensityProps(), ...makeValidationProps() })`.

  No factory declares a `default`: a wrapper resolves an omitted prop where it uses
  it, so that an omitted prop reaches the inner Vuetify component as omitted.
*/
import type { ExtractPropTypes, PropType } from 'vue';

export type Density = 'default' | 'comfortable' | 'compact';

export type InputVariant = 'underlined' | 'outlined' | 'filled' | 'solo' | 'solo-inverted' | 'solo-filled' | 'plain';

export type BtnVariant = 'flat' | 'text' | 'elevated' | 'tonal' | 'outlined' | 'plain';

export type NamedSize = 'x-small' | 'small' | 'default' | 'large' | 'x-large';

/** A named size, or a custom size in pixels. */
export type Size = NamedSize | number;

type ValidateOnEvent = 'blur' | 'input' | 'submit' | 'invalid-input';
type ValidateOnModifier = 'lazy' | 'eager';
export type ValidateOn =
  | ValidateOnEvent
  | ValidateOnModifier
  | `${ValidateOnEvent} ${ValidateOnModifier}`
  | `${ValidateOnModifier} ${ValidateOnEvent}`;

type ValidationResult = string | boolean;
// The wrapper never calls a rule, so any rule function signature is accepted.
export type ValidationRule = ValidationResult | PromiseLike<ValidationResult> | ((value: never) => ValidationResult | PromiseLike<ValidationResult>);

type BlockAnchor = 'top' | 'bottom';
type InlineAnchor = 'start' | 'end' | 'left' | 'right';
export type Anchor =
  | BlockAnchor
  | InlineAnchor
  | 'center'
  | 'center center'
  | `${BlockAnchor} ${InlineAnchor | 'center'}`
  | `${InlineAnchor} ${BlockAnchor | 'center'}`;

/**
 * Distance away from the activator. A pair, or a string of two space-separated numbers, is
 * `[away from the activator, past the activator edge the overlay aligns with]`.
 */
export type OverlayOffset = number | string | [number, number];

/** What an overlay positions itself against; a pair is a viewport `[x, y]` coordinate. */
export type OverlayTarget = string | Element | object | [number, number];

// For values Vue's constructor-based runtime check cannot describe, e.g. an `Element` fails `Object`,
// which only accepts plain objects. `type: null` disables the check; it is typed as `PropType<T>`
// because Vue 2.7's prop typings do not admit `null`.
export function uncheckedProp<T>(): { type: PropType<T> } {
  return { type: null as unknown as PropType<T> };
}

export function makeModelValueProps() {
  return {
    // Unchecked rather than a type list: a `Boolean` entry makes Vue cast an absent prop to `false`.
    modelValue: uncheckedProp<unknown>(),
  };
}

export function makeDensityProps() {
  return {
    density: { type: String as PropType<Density> },
  };
}

export function makeInputVariantProps() {
  return {
    variant: { type: String as PropType<InputVariant> },
  };
}

export function makeBtnVariantProps() {
  return {
    variant: { type: String as PropType<BtnVariant> },
  };
}

export function makeSizeProps() {
  return {
    size: { type: [String, Number] as PropType<Size> },
  };
}

export function makeValidationProps() {
  return {
    rules: { type: Array as PropType<ValidationRule[]> },
    validateOn: { type: String as PropType<ValidateOn> },
    errorMessages: { type: [String, Array] as PropType<string | readonly string[] | null> },
  };
}

export function makeSelectProps() {
  return {
    itemTitle: { type: String },
    itemValue: { type: String },
    closableChips: { type: Boolean },
  };
}

export function makeOverlayLocationProps() {
  return {
    location: { type: String as PropType<Anchor> },
    offset: { type: [Number, String, Array] as PropType<OverlayOffset> },
    target: uncheckedProp<OverlayTarget>(),
  };
}

export type ModelValueProps = ExtractPropTypes<ReturnType<typeof makeModelValueProps>>;
export type DensityProps = ExtractPropTypes<ReturnType<typeof makeDensityProps>>;
export type InputVariantProps = ExtractPropTypes<ReturnType<typeof makeInputVariantProps>>;
export type BtnVariantProps = ExtractPropTypes<ReturnType<typeof makeBtnVariantProps>>;
export type SizeProps = ExtractPropTypes<ReturnType<typeof makeSizeProps>>;
export type ValidationProps = ExtractPropTypes<ReturnType<typeof makeValidationProps>>;
export type SelectProps = ExtractPropTypes<ReturnType<typeof makeSelectProps>>;
export type OverlayLocationProps = ExtractPropTypes<ReturnType<typeof makeOverlayLocationProps>>;
